import type { CSSProperties } from 'react';

/**
 * Easing curves accepted by SegmentedControl's active-pill slide. Maps to a
 * CSS `transition-timing-function` on web and `Easing.bezier(...)` on native.
 * Mirrors Button's animation contract (`ButtonAnimationEasing`).
 */
export type SegmentedControlAnimationEasing =
  | 'linear'
  | 'ease-out'
  | 'ease-out-quad'
  | 'ease-in-out';

/** Default slide duration (ms) — the iOS-idiomatic pill glide. */
export const DEFAULT_ANIMATION_DURATION_MS = 220;
/** Default named easing when no `animationBezier` is supplied. */
export const DEFAULT_ANIMATION_EASING: SegmentedControlAnimationEasing = 'ease-out-quad';

/**
 * `[x1, y1, x2, y2]` control points per named easing. `ease-out-quad` is the DS
 * slide default — a soft quadratic decelerate that reads gentler than the
 * sharper `ease-out`. `linear` / `ease-out` / `ease-in-out` match Button's
 * `CSS_EASING`, and `ease-out-quad` matches the Storybook `BEZIER_PRESETS`
 * value, for cross-component parity.
 */
export const EASING_BEZIER: Record<
  SegmentedControlAnimationEasing,
  readonly [number, number, number, number]
> = {
  linear: [0, 0, 1, 1],
  'ease-out': [0, 0, 0.2, 1],
  'ease-out-quad': [0.25, 0.46, 0.45, 0.94],
  'ease-in-out': [0.4, 0, 0.2, 1],
};

/**
 * The active `[x1, y1, x2, y2]` — an explicit `bezier` wins over the named
 * `easing` (matches the SegmentedControl.types contract).
 */
export function resolveBezierPoints(
  easing: SegmentedControlAnimationEasing | undefined,
  bezier: readonly [number, number, number, number] | undefined,
): readonly [number, number, number, number] {
  return bezier ?? EASING_BEZIER[easing ?? DEFAULT_ANIMATION_EASING];
}

/**
 * Format `[x1, y1, x2, y2]` as a CSS `cubic-bezier(...)` string. Clamps the x
 * components to `[0, 1]` (CSS spec); y may overshoot for back-out / spring
 * curves and is passed through. Shared verbatim with Button's formatter.
 */
export function formatCubicBezier(p: readonly [number, number, number, number]): string {
  const [x1, y1, x2, y2] = p;
  const clampX = (v: number) => Math.min(1, Math.max(0, v));
  return `cubic-bezier(${clampX(x1)}, ${y1}, ${clampX(x2)}, ${y2})`;
}

/**
 * Inline transition style for the WEB active pill (transform + width). Native
 * builds its own `Easing.bezier(...)` from {@link resolveBezierPoints}.
 */
export function pillTransitionStyle(
  duration: number,
  easing: SegmentedControlAnimationEasing | undefined,
  bezier: readonly [number, number, number, number] | undefined,
): CSSProperties {
  return {
    transitionProperty: 'transform, width',
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: formatCubicBezier(resolveBezierPoints(easing, bezier)),
  };
}
